import classnames from 'classnames';
import type { FC } from 'react';
import React, { useContext } from 'react';

import {
  RicosAlignmentContext,
  RicosDirectionHandledContext,
  RicosNodeContext,
  RicosNodeContextProvider,
  RicosViewerContext,
} from './../../../../../ricos-context/index.js';
import type { NodeStyle } from './../../../../../ricos-schema/legacy/wix/rich_content/v1/index.js';
import { TextStyle_TextAlignment } from './../../../../../ricos-schema/legacy/wix/rich_content/v1/index.js';
import type { NodeViewRenderer, NodeViewRendererProps } from './../../../../../ricos-types/index.js';
import { normalizeLineHeight } from './../../../../../_shared/line-spacing-utils.js';

import alignmentStyle from '../../../../statics/alignment-scss.js';
import indentStyle from '../../../../statics/indentation-scss.js';
import Anchorable from '../../utils/Anchorable.js';
import { getPaywallSeoClass, isPaywallSeo } from '../../utils/paywallSeo.js';
import { indentLevelToClassName } from '../consts.js';
import styles from './paragraph-scss.js';

const Paragraph: FC<NodeViewRendererProps> = ({
  node,
  nodeIndex,
  RicosNodesRenderer,
  shouldParagraphApplyTextStyle,
}) => {
  const { seoMode } = useContext(RicosViewerContext);
  const { textAlignment: parentAlignment } = useContext(RicosAlignmentContext);
  const { parentNode } = useContext(RicosNodeContext);
  const directionHandled = useContext(RicosDirectionHandledContext);
  const { indentation = 0 } = node.paragraphData || {};
  const { textAlignment, lineHeight } = node.paragraphData?.textStyle || {};

  const finalParagraphAlignment =
    textAlignment === TextStyle_TextAlignment.AUTO || !textAlignment
      ? parentAlignment
      : textAlignment;

  const paragraphClassName = classnames(
    shouldParagraphApplyTextStyle && styles.text,
    alignmentStyle[finalParagraphAlignment || TextStyle_TextAlignment.AUTO],
    indentStyle[indentLevelToClassName[indentation as $TSFixMeKey<typeof indentLevelToClassName>]],
    !parentNode && isPaywallSeo(seoMode) && getPaywallSeoClass(seoMode?.paywall || {}, nodeIndex)
  );
  const childClassName = classnames(
    styles.child,
    textAlignment === TextStyle_TextAlignment.JUSTIFY && alignmentStyle[textAlignment]
  );
  const isEmpty = !node.nodes?.length;
  const doesEndWithSoftLine =
    !isEmpty && node.nodes?.[node.nodes?.length - 1]?.textData?.text?.slice(-1) === '\n';
  const Tag = isEmpty || !shouldParagraphApplyTextStyle ? 'div' : 'p';
  const nodeStyle: NodeStyle & { lineHeight?: string } = {
    ...node.style,
    ...(lineHeight && { lineHeight: normalizeLineHeight(lineHeight) }),
  };
  const Content = isEmpty ? (
    <br role="presentation" />
  ) : (
    <RicosNodeContextProvider parentNode={node} parentIndex={nodeIndex}>
      <RicosNodesRenderer key={`children-${node.id}`} nodes={node.nodes ?? []} />
    </RicosNodeContextProvider>
  );

  return (
    <Anchorable nodeId={node.id}>
      <Tag className={paragraphClassName} style={nodeStyle} dir={!directionHandled ? 'auto' : ''}>
        <span className={childClassName}>{Content}</span>
        {doesEndWithSoftLine && <br role="presentation" />}
      </Tag>
    </Anchorable>
  );
};

const ParagraphRenderer: NodeViewRenderer = ({ node, ...props }) => {
  return <Paragraph key={node.id} node={node} {...props} />;
};

export default ParagraphRenderer;
